import {
  BasePlan,
  BillingInformation,
  Invoice,
  Limits,
  OrganizationRunMinuteMetrics,
  PaymentMethod,
  ProcessRunMinuteMetrics,
  Subscription,
  Usage,
  WorkspaceRunMinuteMetrics,
} from '~/types/resources';
import { dashboardBillingApi } from '~/utils/api';

export const getPlans = () => dashboardBillingApi.get<BasePlan[]>('/plans');

export const getCurrentSubscription = (organizationId: string) =>
  dashboardBillingApi.get<Subscription>(`/organizations/${organizationId}/subscription`);

export const getCurrentPaymentMethod = (organizationId: string) =>
  dashboardBillingApi.get<PaymentMethod>(`/organizations/${organizationId}/payment-method`);

export const getBillingInformation = (organizationId: string) =>
  dashboardBillingApi.get<BillingInformation>(`/organizations/${organizationId}/billing-info`);

export const updateSubscription = (organizationId: string, update: { tierId: string }) =>
  dashboardBillingApi.post<Subscription>(`/organizations/${organizationId}/subscription`, update);

export const createPaymentMethod = (
  organizationId: string,
  update: { paymentMethodId: string; businessId?: string },
) =>
  dashboardBillingApi.post<PaymentMethod>(
    `/organizations/${organizationId}/payment-method`,
    update,
  );

export const updateBillingInformation = (
  organizationId: string,
  update: Partial<BillingInformation>,
) =>
  dashboardBillingApi.put<PaymentMethod>(`/organizations/${organizationId}/billing-info`, update);

export const deletePaymentMethod = (organizationId: string) =>
  dashboardBillingApi.delete<PaymentMethod>(`/organizations/${organizationId}/payment-method`);

export const getInvoices = (organizationId: string) =>
  dashboardBillingApi.get<Invoice[]>(`/organizations/${organizationId}/invoices`);

export const getUsage = (organizationId: string) =>
  dashboardBillingApi.get<Usage>(`/organizations/${organizationId}/usage`);

export const getOrganizationMetrics = (
  organizationId: string,
  startDate?: Date,
  endDate?: Date,
) => {
  const startYearMonth = startDate?.toISOString().split('T')[0];
  const endYearMonth = endDate?.toISOString().split('T')[0];
  return dashboardBillingApi.get<OrganizationRunMinuteMetrics>(
    `/organizations/${organizationId}/metrics`,
    {
      startDate: startYearMonth,
      endDate: endYearMonth,
    },
  );
};

export const getWorkspaceMetrics = (
  organizationId: string,
  workspaceId: string,
  startDate?: Date,
  endDate?: Date,
) => {
  const startYearMonth = startDate?.toISOString().split('T')[0];
  const endYearMonth = endDate?.toISOString().split('T')[0];
  return dashboardBillingApi.get<WorkspaceRunMinuteMetrics>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/metrics`,
    {
      startDate: startYearMonth,
      endDate: endYearMonth,
    },
  );
};

export const getProcessMetrics = (
  organizationId: string,
  workspaceId: string,
  processId: string,
  startDate?: Date,
  endDate?: Date,
) => {
  const startYearMonth = startDate?.toISOString().split('T')[0];
  const endYearMonth = endDate?.toISOString().split('T')[0];
  return dashboardBillingApi.get<ProcessRunMinuteMetrics>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/processes/${processId}/metrics`,
    {
      startDate: startYearMonth,
      endDate: endYearMonth,
    },
  );
};

export const updateUsageLimit = (organizationId: string, update: Partial<Limits>) =>
  dashboardBillingApi.put<Limits>(`/organizations/${organizationId}/limits`, update);

export const getInvoiceFile = (organizationId: string, invoiceId: string) =>
  dashboardBillingApi.get<Invoice>(`/organizations/${organizationId}/invoices/${invoiceId}/file`);

export const changeCollectionMethod = async (organizationId: string, cardDisabled: boolean) =>
  dashboardBillingApi.put(`/organizations/${organizationId}/collection-method`, { cardDisabled });
